import { useEffect } from 'react';
import { globalEventBus } from '../../../core/events/IEventBus';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import { Logger } from '../../../utils/logger';
import {
  DIAGRAM_MEDIA_REFERENCES_CHANGED_EVENT,
  type DiagramMediaReferencesChangedPayload,
} from '../constants/mediaReleaseEvents';
import {
  acknowledgeDiagramMediaReferenceDelta,
  readDiagramMediaReferenceDeltaState,
  writeDiagramMediaReferenceDeltaState,
} from '../utils/mediaReferenceDeltaQueue';

const log = Logger.getLoggerWithPrefix('MediaReferenceSyncListener');

function normalizeKey(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const key = raw.trim();
  return key ? key : null;
}

export function applyDeltaToPendingSets(
  pendingAdded: Set<string>,
  pendingRemoved: Set<string>,
  delta: { addedKeys?: Iterable<string>; removedKeys?: Iterable<string> },
): void {
  for (const raw of delta.addedKeys ?? []) {
    const key = normalizeKey(raw);
    if (!key) continue;
    pendingAdded.add(key);
    pendingRemoved.delete(key);
  }
  for (const raw of delta.removedKeys ?? []) {
    const key = normalizeKey(raw);
    if (!key) continue;
    if (pendingAdded.has(key)) {
      pendingAdded.delete(key);
      continue;
    }
    pendingRemoved.add(key);
  }
}

export function useDiagramMediaReferenceSyncListener(diagramId: string | undefined): void {
  const authenticatedFetch = useAuthenticatedFetch();
  useEffect(() => {
    const id = diagramId?.trim();
    if (!id || id === 'demo-diagram') return;
    const pendingAdded = new Set<string>();
    const pendingRemoved = new Set<string>();
    let flushTimer: ReturnType<typeof setTimeout> | null = null;

    const persistPendingSnapshot = () => {
      writeDiagramMediaReferenceDeltaState(id, {
        addedKeys: Array.from(pendingAdded),
        removedKeys: Array.from(pendingRemoved),
      });
    };

    const restorePersistedQueue = () => {
      const persisted = readDiagramMediaReferenceDeltaState(id);
      applyDeltaToPendingSets(pendingAdded, pendingRemoved, persisted);
    };

    const flush = () => {
      flushTimer = null;
      const addedKeys = Array.from(pendingAdded);
      const removedKeys = Array.from(pendingRemoved);
      pendingAdded.clear();
      pendingRemoved.clear();
      if (addedKeys.length === 0 && removedKeys.length === 0) {
        return;
      }
      void (async () => {
        try {
          const response = await authenticatedFetch('/api/media/references/sync', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              diagramId: id,
              addedKeys,
              removedKeys,
            }),
          });
          if (!response.ok) {
            const body = await response.text().catch(() => '');
            applyDeltaToPendingSets(pendingAdded, pendingRemoved, { addedKeys, removedKeys });
            persistPendingSnapshot();
            log.error('Media reference sync failed', {
              diagramId: id,
              status: response.status,
              addedCount: addedKeys.length,
              removedCount: removedKeys.length,
              body: body.slice(0, 500),
            });
            return;
          }
          log.info('Media reference sync succeeded', {
            diagramId: id,
            addedCount: addedKeys.length,
            removedCount: removedKeys.length,
          });
          acknowledgeDiagramMediaReferenceDelta(id, { addedKeys, removedKeys });
        } catch (error) {
          applyDeltaToPendingSets(pendingAdded, pendingRemoved, { addedKeys, removedKeys });
          persistPendingSnapshot();
          log.error('Media reference sync request errored', {
            diagramId: id,
            addedCount: addedKeys.length,
            removedCount: removedKeys.length,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      })();
    };

    const scheduleFlush = () => {
      if (flushTimer) return;
      flushTimer = setTimeout(flush, 150);
    };

    const handler = (payload: DiagramMediaReferencesChangedPayload) => {
      if (payload.diagramId !== id) return;
      applyDeltaToPendingSets(pendingAdded, pendingRemoved, {
        addedKeys: payload.addedKeys ?? [],
        removedKeys: payload.removedKeys ?? [],
      });
      persistPendingSnapshot();
      scheduleFlush();
    };
    restorePersistedQueue();
    if (pendingAdded.size > 0 || pendingRemoved.size > 0) {
      scheduleFlush();
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        persistPendingSnapshot();
      }
    };
    const onBeforeUnload = () => {
      persistPendingSnapshot();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('beforeunload', onBeforeUnload);

    const unsubscribe = globalEventBus.on(DIAGRAM_MEDIA_REFERENCES_CHANGED_EVENT, handler);
    return () => {
      if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('beforeunload', onBeforeUnload);
      persistPendingSnapshot();
      flush();
      unsubscribe();
    };
  }, [authenticatedFetch, diagramId]);
}
